import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, $getSelection, $createParagraphNode, TextNode } from "lexical";

export default function PdfPasteFixPlugin({ enabled }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!enabled) return;

    const removeListener = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboard = event.clipboardData;

        let html = clipboard.getData("text/html");
        let text = clipboard.getData("text/plain");

        if (html && html.includes("<p")) {
          const cleanedHTML = cleanPdfHTML(html);
          event.preventDefault();

          editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(cleanedHTML, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);

            // Force justify on all paragraphs
            nodes.forEach((node) => {
              if (node.setFormat) node.setFormat("justify");
              if (node.getChildren) {
                node.getChildren().forEach((child) => {
                  if (child.setFormat) child.setFormat("justify");
                });
              }
            });

            const selection = $getSelection();
            if (selection) selection.insertNodes(nodes);
          });

          return true;
        }

        if (text) {
          const paragraphs = cleanPdfText(text);
          event.preventDefault();

          editor.update(() => {
            const builtHtml = buildHtml(paragraphs);
            const parser = new DOMParser();
            const dom = parser.parseFromString(builtHtml, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);

            nodes.forEach((node) => {
              if (node.setFormat) node.setFormat("justify");
            });

            const selection = $getSelection();
            if (selection) selection.insertNodes(nodes);
          });

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => removeListener();
  }, [editor, enabled]);
  return null;
}

/* ---------------------- helpers ---------------------- */

function cleanPdfHTML(html) {
  const pMatches = [...html.matchAll(/<p[^>]*>(.*?)<\/p>/gis)];
  if (!pMatches.length) return html;

  const cleanParas = [];
  let buffer = "";

  for (let i = 0; i < pMatches.length; i++) {
    const raw = stripTags(pMatches[i][1]).trim();
    if (!raw) {
      if (buffer.trim()) {
        cleanParas.push(buffer.trim());
        buffer = "";
      }
      continue;
    }

    const prevEndsSentence = /[.!?]"?$/.test(buffer.trim());
    const startsMidSentence = /^[a-zåäö]/.test(raw);

    const endsWithHyphen = /-$/.test(buffer.trim());
    if (!buffer) {
      buffer = raw;
      continue;
    }

    if (endsWithHyphen) {
      buffer = buffer.replace(/-$/, "") + raw;
      continue;
    }

    if (!prevEndsSentence || startsMidSentence) {
      buffer += " " + raw;
    } else {
      cleanParas.push(buffer.trim());
      buffer = raw;
    }
  }

  if (buffer.trim()) cleanParas.push(buffer.trim());

  return cleanParas.map((p) => `<p><span>${p}</span></p>`).join("");
}

function stripTags(input) {
  return input.replace(/<[^>]+>/g, "");
}

function escHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const RE_BULLET   = /^[•·]\s*/;
const RE_NUMBERED = /^\d+[.)]\s+/;

// Convert cleaned paragraph strings into HTML, turning bullet/numbered runs
// into proper <ul>/<ol> lists so Lexical creates real ListNode items.
function buildHtml(paragraphs) {
  const parts = [];
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    const isBullet   = RE_BULLET.test(p);
    const isNumbered = RE_NUMBERED.test(p);
    if (isBullet || isNumbered) {
      const tag = isNumbered ? "ol" : "ul";
      const items = [];
      while (i < paragraphs.length) {
        const curr = paragraphs[i];
        if (!RE_BULLET.test(curr) && !RE_NUMBERED.test(curr)) break;
        const content = curr.replace(RE_BULLET, "").replace(RE_NUMBERED, "");
        items.push(`<li>${escHtml(content)}</li>`);
        i++;
      }
      parts.push(`<${tag}>${items.join("")}</${tag}>`);
    } else {
      parts.push(`<p>${escHtml(p)}</p>`);
      i++;
    }
  }
  return parts.join("");
}

// Returns an array of paragraph strings (no HTML).
function cleanPdfText(text) {
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  text = text.replace(/-\n/g, "");

  const lines = text.split("\n");
  const result = [];
  let buffer = "";

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    if (!trimmed) {
      if (buffer) { result.push(buffer); buffer = ""; }
      continue;
    }

    if (!buffer) {
      buffer = trimmed;
      continue;
    }

    const bufferEndsSentence = /[.!?]["']?\s*$/.test(buffer);
    const lineStartsNewParagraph = /^[A-ZÅÄÖ0-9"']/.test(trimmed);
    const lineStartsBullet = /^[•·\-–—*]\s/.test(trimmed) || /^\d+[.)]\s/.test(trimmed);
    const bufferIsBullet = /^[•·\-–—*]\s/.test(buffer) || /^\d+[.)]\s/.test(buffer);

    if (lineStartsBullet || bufferIsBullet || (bufferEndsSentence && lineStartsNewParagraph)) {
      result.push(buffer);
      buffer = trimmed;
    } else {
      buffer += " " + trimmed;
    }
  }

  if (buffer) result.push(buffer);
  return result;
}
